import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, AlertCircle, Info, CheckCircle, Bell } from 'lucide-react';
import { type BureauAlert, type BureauAlertSeverity } from '@/types/credit-scoring';
import { format } from 'date-fns';

interface BureauAlertsWidgetProps {
  alerts: BureauAlert[];
  onReviewAlert: (alertId: string) => void;
  onViewAll: () => void;
}

const getSeverityIcon = (severity: BureauAlertSeverity) => {
  switch (severity) {
    case 'critical':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'warning':
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    case 'info':
      return <Info className="h-4 w-4 text-blue-500" />;
    case 'positive':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
  }
};

const getSeverityColor = (severity: BureauAlertSeverity) => {
  switch (severity) {
    case 'critical':
      return 'text-red-600';
    case 'warning':
      return 'text-orange-600';
    case 'info':
      return 'text-blue-600';
    case 'positive':
      return 'text-green-600';
  }
};

export function BureauAlertsWidget({ alerts, onReviewAlert, onViewAll }: BureauAlertsWidgetProps) {
  const unreviewedAlerts = alerts.filter(alert => !alert.reviewed);
  const topAlerts = unreviewedAlerts.slice(0, 3);

  return (
    <Card className="bg-orange-50 border-orange-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-lg">Bureau Alerts Requiring Action</CardTitle>
            {unreviewedAlerts.length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {unreviewedAlerts.length}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {topAlerts.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
            <p>No alerts requiring action</p>
          </div>
        ) : (
          <>
            {topAlerts.map((alert) => (
              <div
                key={alert.id}
                className="bg-white rounded-lg p-4 border border-orange-200 space-y-2"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{getSeverityIcon(alert.severity)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{alert.customerName}</p>
                        <p className={`text-sm font-semibold ${getSeverityColor(alert.severity)}`}>
                          {alert.title}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onReviewAlert(alert.customerId)}
                      >
                        Review
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(alert.date, 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {unreviewedAlerts.length > 3 && (
              <Button
                variant="outline"
                className="w-full"
                onClick={onViewAll}
              >
                View All {unreviewedAlerts.length} Alerts
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
